import { useState, useCallback, useEffect } from 'react';
import { Plus, Trash2, Edit, Eye, EyeOff, BookOpen } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Switch } from '../../components/ui/switch';
import { blogAPI } from '../../lib/api';
import { toast } from 'sonner';

const BlogManagement = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [form, setForm] = useState({
    title: '', title_en: '', slug: '', excerpt: '', excerpt_en: '', content: '', content_en: '',
    category: 'land-info', tags: '', meta_title: '', meta_description: '', meta_keywords: '', published: true
  });

  const fetchPosts = useCallback(async () => {
    try {
      const res = await blogAPI.getAll({ published_only: false });
      setPosts(res.data);
    } catch (err) { console.error('Failed to fetch blog posts:', err); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchPosts(); }, [fetchPosts]);

  const resetForm = () => {
    setForm({ title: '', title_en: '', slug: '', excerpt: '', excerpt_en: '', content: '', content_en: '', category: 'land-info', tags: '', meta_title: '', meta_description: '', meta_keywords: '', published: true });
    setEditingPost(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
    try {
      if (editingPost) { await blogAPI.update(editingPost.id, payload); toast.success('আর্টিকেল আপডেট হয়েছে'); }
      else { await blogAPI.create(payload); toast.success('আর্টিকেল তৈরি হয়েছে'); }
      fetchPosts(); setIsDialogOpen(false); resetForm();
    } catch (err) { toast.error(err.response?.data?.detail || 'কিছু সমস্যা হয়েছে'); }
  };

  const handleEdit = (post) => {
    setEditingPost(post);
    setForm({ title: post.title, title_en: post.title_en || '', slug: post.slug, excerpt: post.excerpt, excerpt_en: post.excerpt_en || '', content: post.content, content_en: post.content_en || '', category: post.category, tags: (post.tags || []).join(', '), meta_title: post.meta_title || '', meta_description: post.meta_description || '', meta_keywords: post.meta_keywords || '', published: post.published });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('এই আর্টিকেল মুছে ফেলতে চান?')) {
      try { await blogAPI.delete(id); toast.success('আর্টিকেল মুছে ফেলা হয়েছে'); fetchPosts(); }
      catch (err) { toast.error('মুছতে সমস্যা হয়েছে'); }
    }
  };

  const categoryLabels = { 'land-registration': 'জমি রেজিস্ট্রেশন', 'land-records': 'ভূমি রেকর্ড', 'legal-tips': 'আইনি পরামর্শ', 'fees-taxes': 'ফি ও কর', 'land-info': 'ভূমি তথ্য' };

  return (
    <div>
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-white" data-testid="blog-management-title">ব্লগ ম্যানেজমেন্ট</h1>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="bg-[#2E7D32] hover:bg-[#1B5E20] w-full sm:w-auto" data-testid="add-blog-btn"><Plus size={18} className="mr-2" /> নতুন আর্টিকেল</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editingPost ? 'আর্টিকেল সম্পাদনা' : 'নতুন আর্টিকেল'}</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">শিরোনাম (বাংলা)</label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
                <div><label className="block text-sm font-medium mb-1">Title (English)</label><Input value={form.title_en} onChange={(e) => setForm({ ...form, title_en: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Slug</label><Input value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} required placeholder="example-post-slug" /></div>
                <div>
                  <label className="block text-sm font-medium mb-1">ক্যাটাগরি</label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="land-info">ভূমি তথ্য</SelectItem>
                      <SelectItem value="land-registration">জমি রেজিস্ট্রেশন</SelectItem>
                      <SelectItem value="land-records">ভূমি রেকর্ড</SelectItem>
                      <SelectItem value="legal-tips">আইনি পরামর্শ</SelectItem>
                      <SelectItem value="fees-taxes">ফি ও কর</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div><label className="block text-sm font-medium mb-1">সারাংশ (বাংলা)</label><Textarea value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} rows={2} /></div>
              <div><label className="block text-sm font-medium mb-1">কন্টেন্ট (বাংলা, Markdown)</label><Textarea value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={8} required /></div>
              <div><label className="block text-sm font-medium mb-1">Content (English, Markdown)</label><Textarea value={form.content_en} onChange={(e) => setForm({ ...form, content_en: e.target.value })} rows={4} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="block text-sm font-medium mb-1">Tags (কমা দিয়ে)</label><Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="জমি, রেজিস্ট্রেশন" /></div>
                <div><label className="block text-sm font-medium mb-1">Meta Title</label><Input value={form.meta_title} onChange={(e) => setForm({ ...form, meta_title: e.target.value })} /></div>
              </div>
              <div><label className="block text-sm font-medium mb-1">Meta Description</label><Textarea value={form.meta_description} onChange={(e) => setForm({ ...form, meta_description: e.target.value })} rows={2} /></div>
              <div><label className="block text-sm font-medium mb-1">Meta Keywords</label><Input value={form.meta_keywords} onChange={(e) => setForm({ ...form, meta_keywords: e.target.value })} /></div>
              <div className="flex items-center gap-2">
                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} />
                <label className="text-sm">প্রকাশিত</label>
              </div>
              <Button type="submit" className="w-full bg-[#2E7D32] hover:bg-[#1B5E20]">{editingPost ? 'আপডেট করুন' : 'তৈরি করুন'}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="animate-pulse space-y-4">{[1, 2, 3].map(i => <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg" />)}</div>
      ) : posts.length === 0 ? (
        <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
          <BookOpen className="mx-auto text-gray-400 mb-4" size={48} />
          <p className="text-gray-600 dark:text-gray-400">কোনো আর্টিকেল নেই</p>
        </div>
      ) : (
        <div className="space-y-3">
          {posts.map(post => (
            <div key={post.id} className={`bg-white dark:bg-gray-800 rounded-xl p-4 border transition-colors ${post.published ? 'border-gray-200 dark:border-gray-700' : 'border-orange-200 dark:border-orange-800/50 opacity-70'}`} data-testid={`blog-admin-${post.slug}`}>
              <div className="flex flex-col sm:flex-row justify-between items-start gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <h3 className="font-medium text-gray-900 dark:text-white text-sm truncate">{post.title}</h3>
                    <Badge variant="secondary" className="text-xs">{categoryLabels[post.category] || post.category}</Badge>
                    {!post.published && <Badge variant="outline" className="text-xs text-orange-500 border-orange-300">ড্রাফট</Badge>}
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{post.excerpt}</p>
                  <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                    <span><Eye size={10} className="inline mr-1" />{post.views || 0} views</span>
                    <span>{new Date(post.created_at).toLocaleDateString('bn-BD')}</span>
                    {post.tags?.length > 0 && <span>{post.tags.slice(0, 2).join(', ')}</span>}
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(post)} data-testid={`edit-blog-${post.id}`}><Edit size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(post.id)} data-testid={`delete-blog-${post.id}`}><Trash2 size={16} className="text-red-500" /></Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BlogManagement;
